import { Button } from '@/components/ui/primitives';
import { CtaBand, ContextNote, Dateline, FAQList, FeatureGrid, Hero, MarketingPage, PageFooter, StepList } from '@/components/marketing';
import { MEASURE_HREFLANG_ALTERNATES } from '@/pages/landingContent';
import { pageForRoute } from '@/site/pageRegistry';

const PAGE = pageForRoute('/de/pdf-messen');

const STEPS = [
    {
        title: 'Maßstab kalibrieren',
        body: 'Wählen Sie zwei Punkte auf einer bekannten Länge, geben Sie die reale Länge ein und übernehmen Sie den Maßstab. Ein Beispiel: Bei einem Maßstab von 1:50 entspricht 1 cm auf dem Plan 50 cm in Wirklichkeit.',
        image: '/tool-shots/dimension.jpg',
        alt: 'Kalibrierte Bemaßung auf einem Grundriss',
    },
    {
        title: 'Strecke messen',
        body: 'Wählen Sie das Bemaßungswerkzeug und markieren Sie Anfang und Ende der Strecke. Prüfen Sie den Wert an einer weiteren bekannten Länge.',
        image: '/tool-shots/dimension.jpg',
        alt: 'Bemaßungslinie mit Messwert auf einem Plan',
    },
    {
        title: 'Raumfläche und Umfang erfassen',
        body: 'Umranden Sie einen Raum mit einem Polygon oder Rechteck. Für beide Formen zeigt das Werkzeug Fläche und Umfang in m² und m an; eine Ellipse berechnet die Fläche.',
        image: '/tool-shots/polygon.jpg',
        alt: 'Polygon umgrenzt einen Raum auf einem Grundriss',
    },
    {
        title: 'Messwerte als CSV exportieren',
        body: 'Exportieren Sie die Markup-Liste als CSV, um die berechneten Strecken, Flächen und Umfänge weiterzuverwenden.',
        image: '/tool-shots/rectangle.jpg',
        alt: 'Rechteck und Messmarkierungen auf einem PDF-Grundriss',
    },
] as const;

const FAQ_ITEMS = [
    { question: 'Muss die PDF einen Maßstab enthalten?', answer: ['Nein. Kalibrieren Sie den Maßstab mit zwei Punkten auf einer bekannten Länge und deren realem Wert. Prüfen Sie das Ergebnis anschließend an einer zweiten bekannten Länge.'] },
    { question: 'Kann ich Strecken und Räume messen?', answer: ['Ja. Mit dem Bemaßungswerkzeug messen Sie Strecken. Polygone und Rechtecke berechnen Fläche und Umfang; Ellipsen berechnen die Fläche.'] },
    { question: 'Welche Einheiten werden unterstützt?', answer: ['Sie können metrische oder imperiale Einheiten verwenden. Die Werte erscheinen in der Markup-Liste und im CSV-Export.'] },
    { question: 'Wird meine PDF hochgeladen?', answer: ['Nein. Die PDF wird im Browser auf Ihrem Gerät geöffnet. Ein Konto ist dafür nicht erforderlich.'] },
] as const;

export function DePdfMessenPage() {
    return (
        <MarketingPage head={{ ...PAGE.head, canonical: PAGE.canonical, lang: 'de', alternates: MEASURE_HREFLANG_ALTERNATES }}>
            <main>
                <Hero
                    eyebrow="PDF messen"
                    title={<>PDF-Grundrisse messen:<br />Strecken, Flächen und Umfänge.</>}
                    lead="Kalibrieren Sie zuerst den Maßstab. Messen Sie anschließend Strecken und erfassen Sie Raumflächen und Umfänge direkt im PDF."
                    cta={<Button href="/" variant="primary" size="lg">PDF öffnen und messen</Button>}
                    chips={['Metrische Einheiten', 'Maßstab 1:50 und 1:100', 'CSV-Export']}
                />
                <ContextNote text="Die Oberfläche des Tools ist auf Englisch. Ihre PDF wird lokal im Browser geöffnet und nicht hochgeladen." />
                <StepList title="So messen Sie einen PDF-Plan" lead="Vom kalibrierten Maßstab bis zum CSV-Export: vier Schritte für Ihre Messungen." steps={STEPS} />
                <FeatureGrid
                    sectionTitle="Messwerte für Ihren Grundriss"
                    sectionLead="Kalibrieren Sie den Plan und wählen Sie die passende Messform."
                    cards={[
                        { title: 'Strecken', body: 'Das Bemaßungswerkzeug zeigt die reale Länge zwischen zwei Punkten an.' },
                        { title: 'Flächen und Umfänge', body: 'Polygone und Rechtecke berechnen Fläche und Umfang. Ellipsen berechnen die Fläche.' },
                        { title: 'Messwerte exportieren', body: 'Die Markup-Liste enthält Messwerte und lässt sich als CSV exportieren.' },
                    ]}
                />
                <FAQList items={FAQ_ITEMS} />
                <CtaBand
                    title="PDF öffnen und mit dem Messen beginnen"
                    lead="Kalibrieren Sie eine bekannte Länge und messen Sie dann Strecken und Räume."
                    actions={<><Button href="/" variant="primary" size="lg">PDF öffnen</Button><Button href="/de/" variant="ghost" size="lg">Weitere Werkzeuge ansehen</Button></>}
                />
                <Dateline reviewed={PAGE.reviewed} locale="de" />
            </main>
            <PageFooter tagline="PDF-Messungen für Grundrisse im Browser" />
        </MarketingPage>
    );
}
